import { useCallback, useMemo, useState } from 'react'
import { useSurveyStore } from '../stores/surveyStore'
import { useSurveyNavigation } from './useSurveyNavigation'

interface UseSurveyFormStateProps {
  assessmentId: string
  assessmentResponseId: string
  urlPage: number | undefined
  questions: Array<{ id: string; name: string; subName: string }>
  /**
   * Answers loaded from the server (checkpoint restore).
   * Merged under local Zustand state — Zustand wins for the current session,
   * server fills gaps when there is no local state (new device / cleared storage).
   */
  serverAnswers?: Record<string, number>
  /** Called on every page navigation; fire-and-forget server checkpoint save.
   * Receives the current answers and the target page index. */
  onSaveCheckpoint?: (answers: Record<string, number>, page: number) => void
}

export function useSurveyFormState({
  assessmentId,
  assessmentResponseId,
  urlPage,
  questions,
  serverAnswers,
  onSaveCheckpoint,
}: UseSurveyFormStateProps) {
  const { getSurveyProgress, setSurveyProgress, clearSurveyProgress } =
    useSurveyStore()

  const savedProgress = useMemo(
    () => getSurveyProgress(assessmentResponseId),
    [assessmentResponseId, getSurveyProgress],
  )

  // Server answers fill in gaps when there is no local Zustand state (new device /
  // cleared storage). Local Zustand state always takes precedence for the current session.
  const [answers, setAnswers] = useState<Record<string, number>>(() => ({
    ...(serverAnswers ?? {}),
    ...(savedProgress?.answers ?? {}),
  }))

  const questionGroups = useMemo(() => {
    const groups = new Map<string, typeof questions>()
    for (const question of questions) {
      const key = `${question.name}|${question.subName}`
      const existing = groups.get(key)
      if (existing) {
        existing.push(question)
      } else {
        groups.set(key, [question])
      }
    }
    return groups
  }, [questions])

  const groupKeys = useMemo(
    () => Array.from(questionGroups.keys()),
    [questionGroups],
  )

  const { currentPage, handlePageChange } = useSurveyNavigation({
    assessmentId,
    assessmentResponseId,
    urlPage,
    groupKeys,
    questionGroups,
    answers,
    savedProgress,
    questionsCount: questions.length,
    onPersistProgress: (page) =>
      setSurveyProgress(assessmentResponseId, page, answers),
    onSaveCheckpoint,
  })

  const handleAnswerChange = useCallback(
    (questionId: string, value: number) => {
      const newAnswers = {
        ...answers,
        [questionId]: value,
      }
      setAnswers(newAnswers)
      setSurveyProgress(assessmentResponseId, currentPage, newAnswers)
    },
    [answers, assessmentResponseId, currentPage, setSurveyProgress],
  )

  const clearProgress = useCallback(() => {
    clearSurveyProgress(assessmentResponseId)
  }, [assessmentResponseId, clearSurveyProgress])

  return {
    currentPage,
    answers,
    handlePageChange,
    handleAnswerChange,
    clearProgress,
  }
}
